// GoHighLevel API Integration Service
// Centraliza todas las llamadas a la API de GHL para sincronización con página principal

const GHL_API_BASE = "https://services.leadconnectorhq.com";
const GHL_API_VERSION = "2021-07-28";

// NOTA: En producción, estas credenciales deben estar en variables de entorno
// y manejarse desde el backend por seguridad
const GHL_CONFIG = {
  locationId: process.env.VITE_GHL_LOCATION_ID || "", // ID de tu location en GHL
  apiKey: process.env.VITE_GHL_API_KEY || "", // API Key de GHL
};

interface LeadData {
  name: string;
  email: string;
  phone: string;
  company?: string;
  plan: string;
  message?: string;
  source: "funnel" | "main-page";
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

interface GHLContactResponse {
  contact: {
    id: string;
    locationId: string;
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
  };
}

interface GHLOpportunityResponse {
  opportunity: {
    id: string;
    name: string;
    pipelineId: string;
    pipelineStageId: string;
    status: string;
    monetaryValue: number;
  };
}

/**
 * Crea o actualiza un contacto en GoHighLevel
 * Sincroniza con el CRM principal para evitar duplicados
 */
export async function createOrUpdateContact(leadData: LeadData): Promise<GHLContactResponse> {
  const [firstName, ...lastNameParts] = leadData.name.split(" ");
  const lastName = lastNameParts.join(" ");

  const contactPayload = {
    firstName: firstName,
    lastName: lastName || "",
    email: leadData.email,
    phone: leadData.phone,
    companyName: leadData.company || "",
    source: leadData.source,
    tags: [
      leadData.plan,
      "funnel-lead",
      leadData.source === "funnel" ? "landing-page" : "main-website"
    ],
    customFields: [
      {
        key: "plan_selected",
        value: leadData.plan
      },
      {
        key: "lead_message",
        value: leadData.message || ""
      },
      {
        key: "utm_source",
        value: leadData.utmSource || "direct"
      },
      {
        key: "utm_medium",
        value: leadData.utmMedium || "organic"
      },
      {
        key: "utm_campaign",
        value: leadData.utmCampaign || "none"
      }
    ]
  };

  const response = await fetch(`${GHL_API_BASE}/contacts/`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${GHL_CONFIG.apiKey}`,
      "Version": GHL_API_VERSION,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(contactPayload)
  });

  if (!response.ok) {
    throw new Error(`GHL API Error: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Crea una oportunidad en el pipeline de ventas
 * Asigna valor monetario según el plan seleccionado
 */
export async function createOpportunity(
  contactId: string,
  plan: string,
  source: string
): Promise<GHLOpportunityResponse> {
  // Mapeo de planes a valores monetarios (en MXN)
  const planValues: Record<string, number> = {
    "trial-free": 0,
    "starter-mensual": 580,
    "starter-anual": 5800,
    "enterprise-mensual": 2888,
    "enterprise-anual": 34656
  };

  // Pipeline stages IDs (debes configurar estos IDs desde tu cuenta GHL)
  const pipelineStages = {
    "trial-free": process.env.VITE_GHL_STAGE_TRIAL || "stage_trial",
    "starter-mensual": process.env.VITE_GHL_STAGE_STARTER || "stage_starter",
    "starter-anual": process.env.VITE_GHL_STAGE_STARTER || "stage_starter",
    "enterprise-mensual": process.env.VITE_GHL_STAGE_ENTERPRISE || "stage_enterprise",
    "enterprise-anual": process.env.VITE_GHL_STAGE_ENTERPRISE || "stage_enterprise"
  };

  const opportunityPayload = {
    pipelineId: process.env.VITE_GHL_PIPELINE_ID || "default_pipeline",
    locationId: GHL_CONFIG.locationId,
    name: `${plan} - Lead desde ${source}`,
    pipelineStageId: pipelineStages[plan as keyof typeof pipelineStages],
    status: "open",
    contactId: contactId,
    monetaryValue: planValues[plan] || 0,
    assignedTo: process.env.VITE_GHL_ASSIGNED_USER || "",
    source: source
  };

  const response = await fetch(`${GHL_API_BASE}/opportunities/`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${GHL_CONFIG.apiKey}`,
      "Version": GHL_API_VERSION,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(opportunityPayload)
  });

  if (!response.ok) {
    throw new Error(`GHL Opportunity API Error: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Suscribe el contacto a un workflow de nutrición automática
 * Diferencia entre trial gratuito y planes de pago
 */
export async function subscribeToWorkflow(contactId: string, plan: string): Promise<void> {
  // Workflow IDs según el plan (configurar en GHL)
  const workflows: Record<string, string> = {
    "trial-free": process.env.VITE_GHL_WORKFLOW_TRIAL || "",
    "starter-mensual": process.env.VITE_GHL_WORKFLOW_STARTER || "",
    "starter-anual": process.env.VITE_GHL_WORKFLOW_STARTER || "",
    "enterprise-mensual": process.env.VITE_GHL_WORKFLOW_ENTERPRISE || "",
    "enterprise-anual": process.env.VITE_GHL_WORKFLOW_ENTERPRISE || ""
  };

  const workflowId = workflows[plan];
  
  if (!workflowId) {
    console.warn(`No workflow configured for plan: ${plan}`);
    return;
  }

  const response = await fetch(`${GHL_API_BASE}/workflows/${workflowId}/subscribe`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${GHL_CONFIG.apiKey}`,
      "Version": GHL_API_VERSION,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      contactId: contactId
    })
  });

  if (!response.ok) {
    throw new Error(`GHL Workflow API Error: ${response.statusText}`);
  }
}

/**
 * Agenda una cita de implementación en el calendario de GHL
 * Solo para planes de pago (no trial gratuito)
 */
export async function scheduleImplementationCall(
  contactId: string,
  plan: string
): Promise<void> {
  // Solo agendar para planes de pago
  if (plan === "trial-free") {
    return;
  }

  const calendarId = process.env.VITE_GHL_CALENDAR_ID || "";
  
  if (!calendarId) {
    console.warn("No calendar configured for implementation calls");
    return;
  }

  // Fecha sugerida: 48 horas después del registro
  const suggestedDate = new Date();
  suggestedDate.setHours(suggestedDate.getHours() + 48);

  const eventPayload = {
    calendarId: calendarId,
    contactId: contactId,
    title: `Implementación ${plan}`,
    startTime: suggestedDate.toISOString(),
    endTime: new Date(suggestedDate.getTime() + 60 * 60 * 1000).toISOString(), // 1 hora después
    assignedUserId: process.env.VITE_GHL_ASSIGNED_USER || "",
    notes: `Llamada de implementación para plan ${plan}. Preparar accesos y credenciales.`
  };

  const response = await fetch(`${GHL_API_BASE}/calendars/events/`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${GHL_CONFIG.apiKey}`,
      "Version": GHL_API_VERSION,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(eventPayload)
  });

  if (!response.ok) {
    throw new Error(`GHL Calendar API Error: ${response.statusText}`);
  }
}

/**
 * Función principal que orquesta todo el proceso de captura de lead
 * Sincroniza con página principal de GHL
 */
export async function submitLeadToGHL(leadData: LeadData): Promise<{
  success: boolean;
  contactId?: string;
  opportunityId?: string;
  error?: string;
}> {
  try {
    // 1. Crear o actualizar contacto en CRM
    const contactResponse = await createOrUpdateContact(leadData);
    const contactId = contactResponse.contact.id;

    // 2. Crear oportunidad en pipeline
    const opportunityResponse = await createOpportunity(
      contactId,
      leadData.plan,
      leadData.source
    );

    // 3. Suscribir a workflow de nutrición
    await subscribeToWorkflow(contactId, leadData.plan);

    // 4. Agendar llamada de implementación (solo planes de pago)
    await scheduleImplementationCall(contactId, leadData.plan);

    return {
      success: true,
      contactId: contactId,
      opportunityId: opportunityResponse.opportunity.id
    };
  } catch (error) {
    console.error("Error submitting lead to GHL:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
}

/**
 * Captura parámetros UTM de la URL para tracking
 */
export function captureUTMParams(): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
} {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || undefined
  };
}
